import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Paper, Typography, Button, Box, Checkbox, FormControlLabel, Slider, Select, MenuItem } from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase-config';

const AllCars = () => {
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [transmission, setTransmission] = useState('');
  const [city, setCity] = useState('');
  const [carStatus, setCarStatus] = useState('');
  const [allCars, setAllCars] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const q = query(
          collection(db, 'ads'),
          where('status', '==', 'active') // само активни огласи
        );
        const querySnapshot = await getDocs(q);
        const carsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          price: doc.data().price ? `${doc.data().price} €` : 'N/A',
          mileage: doc.data().mileage ? `${doc.data().mileage} km` : '0 km'
        }));
  
        // 🚫 Исклучи огласи што се за rent
        const filtered = carsData.filter(car => car.adType !== 'rent');
  
        // Сортирај: итни > премиум > останати
        filtered.sort((a, b) => {
          if (a.isUrgent && !b.isUrgent) return -1;
          if (!a.isUrgent && b.isUrgent) return 1;
          if (a.isPremium && !b.isPremium) return -1;
          if (!a.isPremium && b.isPremium) return 1;
          return 0;
        });
  
        setAllCars(filtered);
      } catch (error) {
        console.error('Error fetching cars:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);
  

  const carTypes = ['Седан', 'SUV', 'Хечбек', 'Купе', 'Кабриолет'];
  const cities = [
    'Скопје', 'Битола', 'Прилеп', 'Куманово', 'Тетово', 'Охрид', 'Велес', 'Штип', 'Кавадарци', 'Гостивар',
    'Струмица', 'Неготино', 'Кочани', 'Кичево', 'Ресен', 'Гевгелија', 'Струга', 'Делчево', 'Виница', 'Кратово',
    'Крива Паланка', 'Берово', 'Македонски Брод', 'Дебар', 'Демир Капија', 'Демир Хисар', 'Пробиштип', 'Свети Николе',
    'Валандово', 'Богданци', 'Пехчево', 'Радовиш', 'Теарце', 'Желино', 'Липково', 'Чаир', 'Аеродром', 'Гази Баба',
    'Карпош', 'Кисела Вода', 'Центар', 'Ѓорче Петров', 'Сарај', 'Студеничани', 'Арачиново', 'Сопиште', 'Зелениково',
    'Илинден', 'Петровец', 'Чучер-Сандево', 'Зрновци', 'Маврово и Ростуше', 'Ранковце', 'Лозово', 'Новци', 'Новаци',
    'Конче', 'Дојран', 'Василево', 'Босилово', 'Ново Село', 'Карачиново', 'Куклиш', 'Могила', 'Пласница', 'Центар Жупа'
  ];
  const transmissions = ['Автоматски', 'Мануелен'];
  const statuses = ['Нов', 'Користен', 'Изнајмување'];

  const filteredCars = allCars.filter(car => {
    const price = parseInt(car.price?.replace(/[^\d]/g, '') || 0);
    return (
      price >= priceRange[0] && price <= priceRange[1] &&
      (selectedTypes.length === 0 || selectedTypes.includes(car.type)) &&
      (!city || car.city === city) &&
      (!transmission || car.transmission === transmission) &&
      (!carStatus || car.status === carStatus)
    );
  });

  if (loading) {
    return <Typography>Се вчитува...</Typography>;
  }

  return (
    <div className="all-cars-page">
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <Typography variant="h4" gutterBottom>Сите Автомобили</Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, position: 'sticky', top: '20px' }}>
              <Typography variant="h6" gutterBottom>Филтри</Typography>

              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>Цена (€)</Typography>
                <Slider
                  value={priceRange}
                  onChange={(e, newValue) => setPriceRange(newValue)}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100000}
                  step={1000}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>{priceRange[0]} €</Typography>
                  <Typography>{priceRange[1]} €</Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>Тип на автомобил</Typography>
                {carTypes.map((type) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox 
                        checked={selectedTypes.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTypes([...selectedTypes, type]);
                          } else {
                            setSelectedTypes(selectedTypes.filter(t => t !== type));
                          }
                        }}
                      />
                    }
                    label={type}
                  />
                ))}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>Град</Typography>
                <Select
                  fullWidth
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Сите градови</MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>{city}</MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>Менувач</Typography>
                <Select
                  fullWidth
                  value={transmission}
                  onChange={(e) => setTransmission(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Сите типови</MenuItem>
                  {transmissions.map((trans) => (
                    <MenuItem key={trans} value={trans}>{trans}</MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>Состојба</Typography>
                <Select
                  fullWidth
                  value={carStatus}
                  onChange={(e) => setCarStatus(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Сите</MenuItem>
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
              {filteredCars.length} {filteredCars.length === 1 ? 'автомобил' : 'автомобили'} пронајдени
            </Typography>
            <Grid container spacing={3}>
              {filteredCars.map((car) => (
                <Grid item xs={12} sm={6} md={4} key={car.id}>
                  <Paper sx={{ p: 2, height: '100%', position: 'relative', border: car.isUrgent ? '2px solid red' : 'none' }}>
                    {car.isUrgent && (
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        left: -32,
                        backgroundColor: 'red',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        transform: 'rotate(-45deg)',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        zIndex: 1,
                        width: 100,
                        textAlign: 'center'
                      }}>
                        ИТНО
                      </Box>
                    )}
                    <Box sx={{ height: '150px', bgcolor: '#f5f5f5', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {car.images?.[0] ? (
                        <img 
                        src={car.images[car.coverPhotoIndex || 0]} 
                          alt={car.model} 
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <Typography>Нема слика</Typography>
                      )}
                    </Box>
                    <Typography variant="h6">{car.brand} {car.model}</Typography>
                    <Typography color="primary" sx={{ fontWeight: 'bold' }}>{car.price}</Typography>
                    <Typography variant="body2">{car.city} • {car.mileage} • {car.transmission}</Typography>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      sx={{ mt: 2 }} 
                      onClick={() => navigate(`/cars/${car.id}`)}
                    >
                      Види детали
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </main>
    </div>
  );
};

export default AllCars;
